/** API central Edusico (Cloud Run detrás de api.edusico.cl). */
export const EDUSICO_API_PRODUCTION_URL = 'https://api.edusico.cl';

/** Backend Sala en Cloud Run — Hosting reescribe /api con timeout de 60s (502 en generaciones largas). */
export const SALA_CLOUD_RUN_URL =
  'https://edusico-interactive-room-7121991475.us-central1.run.app';

function isSalaProductionHost(host: string): boolean {
  return host === 'sala.edusico.cl' || host === 'edusico-sala.web.app';
}

function isLocalDevUrl(url: string): boolean {
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/?$/i.test(url.trim());
}

function readEnvApiUrl(): string {
  const candidates = [
    import.meta.env.VITE_INTERACTIVE_ROOM_URL,
    import.meta.env.VITE_EDUSICO_API_URL,
  ];
  for (const raw of candidates) {
    const url = String(raw || '').trim();
    if (!url) continue;
    if (import.meta.env.PROD && isLocalDevUrl(url)) continue;
    return url;
  }
  return '';
}

function resolveProductionApiUrl(): string {
  if (typeof window === 'undefined') return EDUSICO_API_PRODUCTION_URL;

  const host = window.location.hostname.toLowerCase();
  // Life OS y otros frontends en subdominio distinto al API
  if (host === 'lifeos.edusico.cl') {
    return EDUSICO_API_PRODUCTION_URL;
  }
  // Generación IA > 60s: no usar rewrite de Hosting (502). Ir directo a Cloud Run.
  if (isSalaProductionHost(host)) {
    return SALA_CLOUD_RUN_URL;
  }
  // Sala u otros con rewrites /api en el mismo origen
  return window.location.origin;
}

/**
 * En DEV, Sala (Vite :5180) proxya /api y /socket.io a :8790.
 * Preferir same-origin evita Failed to fetch cross-port en el navegador.
 */
function resolveDevBrowserUrl(candidate: string): string {
  if (typeof window === 'undefined') return candidate || 'http://127.0.0.1:8790';
  if (!candidate || isLocalDevUrl(candidate)) {
    return window.location.origin;
  }
  return candidate;
}

/** URL base del servidor interactive-room (REST + Socket.IO). */
export function resolveInteractiveRoomUrl(override?: string): string {
  if (override?.trim()) {
    return import.meta.env.DEV ? resolveDevBrowserUrl(override.trim()) : override.trim();
  }

  try {
    const saved = localStorage.getItem('interactive_room_server_url');
    if (saved?.trim() && !(import.meta.env.PROD && isLocalDevUrl(saved))) {
      const savedUrl = saved.trim();
      // Same-origin Sala Hosting → 502 en /api/exams/generate (timeout 60s).
      if (
        import.meta.env.PROD &&
        /https?:\/\/(sala\.edusico\.cl|edusico-sala\.web\.app)\/?$/i.test(savedUrl)
      ) {
        /* ignore */
      } else {
        return import.meta.env.DEV ? resolveDevBrowserUrl(savedUrl) : savedUrl;
      }
    }
  } catch {
    /* ignore */
  }

  const envUrl = readEnvApiUrl();
  if (envUrl) {
    return import.meta.env.DEV ? resolveDevBrowserUrl(envUrl) : envUrl;
  }

  if (import.meta.env.PROD) {
    return resolveProductionApiUrl();
  }

  return resolveDevBrowserUrl('');
}

/** Mensaje de error de conexión según entorno (sin mencionar puerto 8790 en prod). */
export function interactiveRoomConnectionErrorMessage(): string {
  if (import.meta.env.PROD) {
    return 'No se pudo conectar con el servidor. Comprueba tu conexión e intenta de nuevo.';
  }
  return 'No se pudo conectar con el servidor de Sala. Reinicia `npm run dev:sala` (debe quedar activo el proceso SERVER en :8790) y recarga.';
}
